import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserCheck, AlertCircle, X } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';
import { getBackendUrl } from '../utils/getBackendUrl';

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

export const PendingUsersAlert = ({ user }) => {
  const navigate = useNavigate();
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchPendingUsers();
      // Check every 30 seconds
      const interval = setInterval(fetchPendingUsers, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const fetchPendingUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/pending-users`);
      setPendingUsers(response.data || []);
      setPendingCount(response.data?.length || 0);
      
      // Reset dismissed state if new users appear
      if (response.data?.length > 0) {
        const lastDismissedCount = localStorage.getItem('dismissedPendingCount');
        if (response.data.length > parseInt(lastDismissedCount || '0')) {
          setDismissed(false);
        }
      }
    } catch (error) {
      console.error('Failed to fetch pending users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('dismissedPendingCount', pendingCount.toString());
  };

  const handleViewUsers = () => {
    navigate('/settings?tab=users');
  };

  if (user?.role !== 'admin' || loading || pendingCount === 0 || dismissed) {
    return null;
  }

  return (
    <div className="bg-gradient-to-r from-orange-500 to-red-600 rounded-lg shadow-lg p-4 relative overflow-hidden animate-pulse-subtle">
      {/* Animated background */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-0 left-0 w-32 h-32 bg-white rounded-full -translate-x-1/2 -translate-y-1/2 animate-pulse" />
        <div className="absolute bottom-0 right-0 w-32 h-32 bg-white rounded-full translate-x-1/2 translate-y-1/2 animate-pulse" />
      </div>

      <div className="relative flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 flex-1">
          <div className="bg-white/20 p-3 rounded-lg backdrop-blur-sm">
            <AlertCircle className="w-6 h-6 text-white animate-bounce" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-white font-bold text-lg">
                {pendingCount} {pendingCount === 1 ? 'User' : 'Users'} Waiting for Approval
              </h3>
              <span className="bg-white text-red-600 font-bold text-xs px-2 py-1 rounded-full">
                NEW
              </span>
            </div>
            <p className="text-white/95 text-sm mb-3">
              {pendingUsers.slice(0, 3).map((u, i) => (
                <span key={u.id}>
                  <strong>{u.name}</strong> ({u.email})
                  {u.suggested_by_name && <em> — suggested by {u.suggested_by_name}</em>}
                  {i < Math.min(2, pendingUsers.length - 1) ? ', ' : ''}
                </span>
              ))}
              {pendingCount > 3 && ` and ${pendingCount - 3} more...`}
            </p>
            <div className="flex gap-2">
              <Button
                onClick={handleViewUsers}
                className="bg-white text-red-600 hover:bg-red-50 font-semibold shadow-md"
              >
                <UserCheck className="w-4 h-4 mr-2" />
                Review Users
              </Button>
            </div>
          </div>
        </div>

        <button
          onClick={handleDismiss}
          className="text-white/80 hover:text-white p-2 rounded-lg hover:bg-white/10 transition"
          aria-label="Dismiss"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};
